/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * forum-access.ts
 *
 * T-1: gates Support Forum sign-in on the customer having chosen a public
 * display name (decision D-6 — never derived from the email address).
 *
 * Follows the same X-Session-Token fetch pattern as http-client.ts, since
 * these calls hit the same DRF endpoints (AllauthSessionTokenAuthentication),
 * not the allauth headless base path.
 */

import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { configuration } from "./configuration";
import { authClient, getSessionToken } from "./auth-client";
import { safeLocalStorage } from "@/helper/safeLocalStorage";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import { FORUM_PATH, getForumUrl } from "@/utils/siteConfig";

export interface ForumDisplayNameCheck {
  hasDisplayName: boolean;
  userId: string | null;
}

/**
 * Looks up whether the signed-in customer has already set a forum display
 * name. Returns hasDisplayName: false (not an error) when signed out, so
 * callers can treat "no session" and "no display name" the same way if
 * they choose to — though in practice callers should only invoke this
 * when a session is already known to exist.
 */
export async function checkForumDisplayName(userId: string): Promise<boolean> {
  const sessionToken = getSessionToken();
  if (!sessionToken) return false;
  try {
    const res = await fetch(`${configuration.backendURL}/api/users/${userId}`, {
      headers: { "X-Session-Token": sessionToken },
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { forum_display_name?: string };
    return !!data.forum_display_name;
  } catch {
    return false;
  }
}

export interface SetForumDisplayNameResult {
  ok: boolean;
  error?: string;
}

/**
 * Sets the customer's chosen forum display name via PATCH /api/users/<id>.
 * Surfaces the backend's validation message (format, email-derived rejection,
 * or the case-insensitive duplicate check) rather than a generic failure.
 */
export async function setForumDisplayName(
  userId: string,
  displayName: string,
): Promise<SetForumDisplayNameResult> {
  const sessionToken = getSessionToken();
  if (!sessionToken) return { ok: false, error: "not_signed_in" };
  try {
    const res = await fetch(`${configuration.backendURL}/api/users/${userId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-Session-Token": sessionToken,
      },
      body: JSON.stringify({ forum_display_name: displayName }),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as {
        forum_display_name?: string[];
      };
      return { ok: false, error: data.forum_display_name?.[0] ?? "invalid" };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "network_error" };
  }
}

/**
 * Mints the forum session cookie (POST /api/auth/forum-session). Must only
 * be called once a display name is confirmed set — the backend rejects the
 * request with 400 otherwise. Cookie-setting is handled entirely by the
 * backend response; there is nothing further for the caller to store.
 */
/**
 * T-1: the flag that records "a forum cookie was minted in this browser".
 *
 * The cookie itself is HttpOnly, so script cannot check whether it exists. This
 * flag is the only thing that lets the sync tell two situations apart:
 *
 *   no cookie, and one was minted   -> the customer signed out of the forum
 *   no cookie, and none was minted  -> the customer never opened the forum
 *
 * Without it the second case looks identical to the first, and every
 * forum-eligible customer who has never visited the forum would be signed out
 * of the website they are actively using.
 */
const FORUM_COOKIE_MINTED_KEY = "deskshare_forum_cookie_minted";

export function markForumCookieMinted(): void {
  safeLocalStorage.setItem(FORUM_COOKIE_MINTED_KEY, "1");
}

export function hasForumCookieMinted(): boolean {
  return safeLocalStorage.getItem(FORUM_COOKIE_MINTED_KEY) === "1";
}

export function clearForumCookieMinted(): void {
  safeLocalStorage.removeItem(FORUM_COOKIE_MINTED_KEY);
}

export async function mintForumSession(): Promise<boolean> {
  const sessionToken = getSessionToken();
  if (!sessionToken) return false;
  try {
    const res = await fetch(
      `${configuration.backendURL}/api/auth/forum-session`,
      {
        method: "POST",
        headers: { "X-Session-Token": sessionToken },
        credentials: "include",
      },
    );
    // T-1: record the mint so the sync knows a missing cookie means something.
    if (res.ok) markForumCookieMinted();
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Where the display-name collection screen sends the customer back to once
 * a name is set — always the forum itself unless a different redirect is
 * requested.
 */
export const FORUM_DISPLAY_NAME_PATH = "/auth/forum-display-name";

// ---------------------------------------------------------------------------
// P-1 — preparing the shared forum session as part of signing in
// ---------------------------------------------------------------------------

/**
 * True when `raw` names the Support Forum as a post-login destination.
 *
 * Covers both shapes: the proxied /forums path on the marketing host, and a
 * separate forum host named by the forum address setting. On a feature
 * environment that setting often still names production while the redirect
 * targets /forums on the feature host, so a match on EITHER shape counts —
 * matching only the configured host is what would make the fix silently do
 * nothing everywhere except production.
 *
 * The address is read through getForumUrl() rather than from the environment
 * directly. That accessor exists because this value once had two names, only
 * one of which was documented: setting the documented one appeared to do
 * nothing and a safe-redirect rule written for a future forum host was
 * permanently dead. One name, one reader.
 *
 * (The redirect-guard module deliberately does read the variable by hand,
 * because it is mocked in almost every authentication test and importing the
 * address module there would break any test supplying a partial mock. That
 * reasoning applies to the guard and not to this file, which no test mocks. If
 * a test does start mocking the address module, mock it here too.)
 */
export function isForumRedirectUrl(raw: string): boolean {
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return false;
  }

  const forumEnv = getForumUrl();
  if (forumEnv.startsWith("http")) {
    try {
      const forumBase = new URL(forumEnv);
      if (url.origin === forumBase.origin) {
        const basePath = forumBase.pathname.replace(/\/$/, "");
        if (!basePath) return true;
        return (
          url.pathname === basePath || url.pathname.startsWith(`${basePath}/`)
        );
      }
    } catch {
      /* misconfigured value - the same-origin rule below still applies */
    }
  }

  const forumPath = FORUM_PATH.replace(/\/$/, "");
  return (
    url.origin === window.location.origin &&
    (url.pathname === forumPath || url.pathname.startsWith(`${forumPath}/`))
  );
}

/**
 * P-1: before sending a newly signed-in customer to the forum, route them
 * through the display-name screen. That page already creates the shared forum
 * session and then performs a full navigation to ?redirect= — the same proven
 * path the website header uses — and it also handles the customer who has not
 * chosen a public forum name yet, which a direct mintForumSession() call would
 * not.
 *
 * Deliberately SYNCHRONOUS, and deliberately unable to fail.
 *
 * The obvious implementation awaits authClient.getSession() to find out whether
 * there is a session to prepare. That is a network request, and a network
 * request on this path can strand a customer on the sign-in page after a
 * sign-in that actually worked — a dropped connection, a slow backend or a
 * captive portal would reject the promise and the navigation would never
 * happen. It is also unnecessary: the session token is already in local
 * storage and getSessionToken() reads it without a request. The forum step is
 * an optimisation and must never be able to stop the navigation.
 *
 * Returns the original destination unchanged whenever it cannot help, so every
 * caller ends in a navigation.
 */
export function prepareForumAuthenticatedRedirect(targetUrl: string): string {
  if (!isForumRedirectUrl(targetUrl)) return targetUrl;

  if (!getSessionToken()) {
    // Reaching the forum signed out, with nothing anywhere recording why, is
    // the exact shape of the fault being fixed here. One warning makes a
    // recurrence visible instead of invisible.
    Sentry.captureMessage("forum_redirect_without_session", {
      level: "warning",
      tags: { area: "forum-signin" },
    });
    return targetUrl;
  }

  return (
    `${AddLangToPath(FORUM_DISPLAY_NAME_PATH)}` +
    `?redirect=${encodeURIComponent(targetUrl)}`
  );
}

// ---------------------------------------------------------------------------
// useForumLinkHref
// ---------------------------------------------------------------------------

/**
 * Decides where the header/support-page "Support Forum" link should point:
 *
 *  - Signed out: FORUM_PATH unchanged — anonymous forum browsing is allowed,
 *    only the account-linking sign-in is gated.
 *  - Signed in, display name already set: FORUM_PATH unchanged, and the
 *    forum session cookie is minted quietly in the background so it is
 *    already present by the time the customer clicks through.
 *  - Signed in, no display name yet: the display-name collection screen,
 *    with ?redirect= pointing back at the forum — decision D-6, forum
 *    sign-in must not ship a name derived from the email address, so the
 *    customer must choose one before the link can take them there signed in.
 *
 * Deliberately resolved once per mount rather than intercepting the click:
 * the forum link is a plain <a href> for Traefik-proxy reasons (see
 * FORUM_PATH's own doc comment) and must stay that way, not become a
 * click-handler-driven navigation.
 *
 * A-17: the resolution is cached for the lifetime of the page. This hook is
 * called from the Header, which mounts on every full page load and can remount
 * during navigation, and it fires three SEQUENTIAL network requests -- a
 * session read, a user read, then a cookie mint -- purely to decide one link's
 * href. None of those answers change while the customer stays signed in as the
 * same person, so repeating them costs latency and gives nothing back. The
 * cache is keyed on the session token, so signing out and back in as somebody
 * else re-resolves rather than reusing the previous customer's answer.
 */

/**
 * Cached resolution, keyed by the session token that produced it.
 *
 * Module-level state survives between tests. `resetForumLinkCache()` is
 * exported for that reason and is called from the test setup -- without it, one
 * test's resolution leaks into the next and the failure looks like a race.
 */
let cachedForumHref: { token: string; href: string } | null = null;
let inFlightToken: string | null = null;

/** Clear the cached forum-link resolution. Used on sign-out and in tests. */
export function resetForumLinkCache(): void {
  cachedForumHref = null;
  inFlightToken = null;
  // T-1: the mint flag must not survive a sign-out. If it did, the customer
  // would sign in again, have no forum cookie yet, be reported as not synced,
  // and be signed straight back out - a loop they cannot escape without
  // clearing site data.
  //
  // auth-client's signOut() already calls this function, so clearing it here is
  // enough and no new call site is needed. That was verified rather than
  // assumed, and there is a test asserting the flag is gone after signOut() so
  // the link between the two cannot be broken by someone tidying that import
  // away.
  clearForumCookieMinted();
}
export function useForumLinkHref(
  forumPath: string,
  addLangToPath: (path: string) => string,
): string {
  const token = getSessionToken();
  const [href, setHref] = useState(
    cachedForumHref?.token === token ? cachedForumHref.href : forumPath,
  );

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      // A signed-out visitor needs no resolution at all, and any cached answer
      // belonged to the previous customer.
      resetForumLinkCache();
      return;
    }

    // Already resolved for this session, or a resolution is already running
    // for it. Either way there is nothing to ask the server again.
    if (cachedForumHref?.token === token) {
      setHref(cachedForumHref.href);
      return;
    }
    if (inFlightToken === token) return;
    inFlightToken = token;

    void authClient.getSession().then(async (result) => {
      if (!result.data) {
        inFlightToken = null;
        return;
      }
      const userId = result.data.user.id;
      const hasName = await checkForumDisplayName(userId);
      const resolved = hasName
        ? forumPath
        : `${addLangToPath(FORUM_DISPLAY_NAME_PATH)}?redirect=${encodeURIComponent(forumPath)}`;

      // Cache before the cancellation guard: the answer is valid for the whole
      // page even if THIS component unmounted while the request was in flight.
      cachedForumHref = { token, href: resolved };
      inFlightToken = null;

      if (hasName) void mintForumSession();
      if (cancelled) return;
      setHref(resolved);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- forumPath/addLangToPath are stable across the component's lifetime
  }, [token]);

  return href;
}
